import { IFilter, IPage } from "@/api/api";
import { deleteInvoiceItem, IInvoice, IInvoiceItem } from "@/api/invoice";
import { deleteCurrentInvoice } from "@/api/store";
import { Delete } from "@mui/icons-material";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

interface Props {
    invoice_item_id: string;
    invoiceTableQueryKey: (
        | string
        | number
        | IFilter
        | {
              page: number;
              pageSize: number;
          }
    )[];
    totalItemsCount: number;
    currentStoreId: number;
    invoice: IInvoice | null;
    onModalClose: () => void;
}
export const InvoiceDeleteIcon = ({
    invoice_item_id,
    invoiceTableQueryKey,
    totalItemsCount,
    currentStoreId,
    invoice,
    onModalClose,
}: Props) => {
    const [open, setOpen] = useState(false);
    const queryClient = useQueryClient();

    const { mutate: deleteInvoiceMutation } = useMutation({
        mutationFn: () => {
            return deleteCurrentInvoice(currentStoreId);
        },
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: invoiceTableQueryKey });
            const previous = queryClient.getQueryData(invoiceTableQueryKey);
            queryClient.setQueryData(
                invoiceTableQueryKey,
                (old: IPage<IInvoice>) => {
                    if (old?.items) {
                        return {
                            ...old,
                            items: old.items.filter(
                                (inv: IInvoice) => inv.id !== invoice?.id
                            ),
                        };
                    }
                    return old;
                }
            );
            return { previous };
        },
        onError: (_, _vars, context) => {
            if (context?.previous) {
                queryClient.setQueryData(
                    invoiceTableQueryKey,
                    context.previous
                );
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: invoiceTableQueryKey });
        },
    });

    const { mutate: deleteItemMutation } = useMutation({
        mutationFn: (invoice_item_id: string) => {
            return deleteInvoiceItem(invoice_item_id);
        },

        onMutate: async (invoice_item_id) => {
            await queryClient.cancelQueries({ queryKey: ["selected-invoice"] });

            // Snapshot current invoice
            const previous = queryClient.getQueryData(["selected-invoice"]);

            queryClient.setQueryData(["selected-invoice"], (old: IInvoice) => {
                if (old?.lines && Array.isArray(old.lines.data)) {
                    // Handle paginated response
                    return {
                        ...old,
                        lines: {
                            ...old.lines,
                            data: old.lines.data.filter(
                                (item: IInvoiceItem) =>
                                    item.id !== invoice_item_id
                            ),
                        },
                    };
                }
                return old;
            });

            // Return rollback context
            return { previous };
        },
        onError: (_, _vars, context) => {
            // Rollback on failure
            if (context?.previous) {
                queryClient.setQueryData(
                    ["selected-invoice"],
                    context.previous
                );
            }
        },
        onSuccess: () => {
            // If this was the last item, cascade delete the parent invoice
            if (totalItemsCount === 1) {
                deleteInvoiceMutation(undefined, {
                    onSuccess: () => {
                        onModalClose();
                    },
                });
            }
        },
        onSettled: () => {
            // Always refetch to sync with server
            queryClient.invalidateQueries({ queryKey: ["selected-invoice"] });
            queryClient.invalidateQueries({ queryKey: invoiceTableQueryKey });
        },
    });

    const handleDelete = () => {
        deleteItemMutation(invoice_item_id);
        setOpen(false);
    };

    return (
        <>
            <IconButton
                color="error"
                onClick={() => {
                    setOpen(true);
                }}
                sx={{ pl: "8px" }}
            >
                <Delete />
            </IconButton>
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle id="alert-dialog-title">
                    Delete Draft Invoice Item?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete the invoice item?
                        <br />
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button color="error" onClick={handleDelete} autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
